import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight, ArrowUpRight, BadgeCheck, ShieldCheck, Building2,
  Banknote, FileSpreadsheet, ClipboardList, Users, Clock, AlertTriangle,
} from "lucide-react";
import ShiftsLayout from "../../components/shifts/ShiftsLayout";
import HowItWorks from "../../components/shifts/HowItWorks";
import PageMeta from "../../components/seo/PageMeta";
import { HOW_IT_WORKS, SHIFTS_CTAS } from "../../data/marketplaceData";

const REASONS = [
  { Icon: ShieldCheck,    title: "Joint Commission-aligned",   blurb: "Every clinician runs through TRITAL Care's 6-gate compliance pipeline before they're match-eligible." },
  { Icon: Banknote,       title: "Bill-rate transparency",      blurb: "One all-in hourly rate per shift. No surprise margins. Net-30 invoicing with line-item breakdowns." },
  { Icon: FileSpreadsheet,title: "VMS / scheduler integrations",blurb: "We sync with Kronos, Workday, Symplr, ShiftBoard, Smartlinx, Smart Square — and feed your AP system clean exports." },
  { Icon: ClipboardList,  title: "Unit-level credentialing rules",blurb: "Set required competencies, immunizations, drug-screen window per unit. TRITAL ShiftBridge™ blocks claims that fail." },
  { Icon: Users,          title: "Float-pool optimization",     blurb: "Build an internal float pool inside the same platform — TRITAL Care converts your top travelers to perm float at facility cost basis." },
  { Icon: AlertTriangle,  title: "Cancellation discipline",     blurb: "Repeated late-cancel clinicians lose match priority. You set the rule. We enforce it. Audit log included." },
];

const PRICING = [
  { tier: "Starter",   blurb: "Single facility · low-volume PRN", lineItems: ["Up to 25 shifts / month","Standard compliance pack","Email support (1-hour SLA)"], cta: "Talk to sales" },
  { tier: "Growth",    blurb: "Multi-unit hospitals & SNF chains", lineItems: ["Unlimited PRN shifts","Custom credentialing rules per unit","VMS sync + dedicated CSM"], cta: "Request quote", featured: true },
  { tier: "Enterprise",blurb: "Health systems · IDNs · MSPs",      lineItems: ["MSP / VMS hybrid mode","SOC 2 controls + custom DPA","Account-manager + clinical liaison"], cta: "Talk to enterprise" },
];

export default function ForFacilities() {
  return (
    <ShiftsLayout>
      <PageMeta
        title="For Facilities — TRITAL ShiftBridge™ by TRITAL Care®"
        description="Post PRN shifts in 90 seconds and get 92% fill at 7 days. Bill-rate transparency, W-2 clinicians, Net-30 invoicing — built by TRITAL Care®."
        canonical="https://www.tritalcare.com/shiftbridge/facilities"
      />

      {/* HERO */}
      <section className="relative overflow-hidden bg-trital-mesh">
        <div className="absolute inset-0 dot-grid opacity-60 pointer-events-none" />
        <div className="relative max-w-[1400px] mx-auto px-5 lg:px-8 py-20 lg:py-28">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white border border-slate-200 shadow-card">
              <Building2 className="w-3.5 h-3.5 text-trital-red" strokeWidth={1.8} />
              <span className="overline text-trital-navy">For facilities</span>
            </div>
            <h1 className="mt-6 font-display font-black text-5xl lg:text-[64px] leading-[1.02] tracking-tight text-slate-900 text-balance">
              Fill the shift.<br/>
              <span className="text-trital-navy">Not the procurement queue.</span>
            </h1>
            <p className="mt-5 text-slate-600 max-w-[60ch] leading-relaxed text-lg">
              TRITAL ShiftBridge™ is the W-2 PRN marketplace operated by TRITAL Care®. Every clinician is credential-cleared, hourly rates are transparent, and you get one consolidated invoice — across every unit, every week.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <Link
                to={SHIFTS_CTAS.facilitySignup}
                className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full bg-trital-navy text-white text-[14px] font-semibold btn-lift hover:bg-trital-navy-deep shadow-[0_8px_30px_-8px_rgba(19,39,122,0.5)]"
                data-testid="ff-signup-cta"
              >
                Create facility account <ArrowRight className="w-4 h-4" strokeWidth={1.8} />
              </Link>
              <Link
                to="/contact"
                className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full border border-slate-300 text-slate-700 text-[14px] font-semibold hover:border-trital-navy hover:text-trital-navy hover:bg-white"
              >
                Talk to enterprise <ArrowUpRight className="w-4 h-4 text-trital-red" strokeWidth={1.8} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <HowItWorks
        eyebrow="Four-step facility flow"
        title="From signup to first filled shift in two business days."
        steps={HOW_IT_WORKS.facility}
        cta={{ label: "Sign up as a facility", to: SHIFTS_CTAS.facilitySignup }}
        testid="ff-how-it-works"
      />

      {/* WHY US */}
      <section className="max-w-[1400px] mx-auto px-5 lg:px-8 py-20 lg:py-24" data-testid="ff-why">
        <div className="max-w-3xl">
          <div className="overline text-trital-red">Why facilities choose us</div>
          <h2 className="mt-3 font-display font-black text-4xl lg:text-[44px] tracking-tight leading-[1.05] text-slate-900 text-balance">
            Procurement-grade. Operator-built.
          </h2>
        </div>
        <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {REASONS.map((r, i) => (
            <motion.div
              key={r.title}
              initial={{ opacity: 0, y: 8 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.3, delay: i * 0.04 }}
              className="rounded-3xl bg-white border border-slate-200 shadow-card p-6"
              data-testid={`ff-reason-${i}`}
            >
              <div className="w-11 h-11 rounded-xl bg-trital-medical-blue-soft text-trital-navy flex items-center justify-center"><r.Icon className="w-5 h-5" strokeWidth={1.8} /></div>
              <div className="mt-5 font-display font-bold text-[17px] text-slate-900">{r.title}</div>
              <p className="mt-2 text-[13.5px] text-slate-600 leading-relaxed">{r.blurb}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* SLA / METRICS BAND */}
      <section className="bg-trital-navy-ink text-white relative overflow-hidden" data-testid="ff-sla">
        <div className="absolute inset-0 bg-trital-navy-mesh opacity-85" />
        <div className="absolute inset-0 dot-grid opacity-10" />
        <div className="relative max-w-[1400px] mx-auto px-5 lg:px-8 py-20 lg:py-24">
          <div className="max-w-3xl">
            <div className="overline text-trital-red">Service levels</div>
            <h2 className="mt-3 font-display font-black text-4xl lg:text-[44px] tracking-tight leading-[1.05] text-white text-balance">
              The numbers we put in the contract.
            </h2>
          </div>
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { num: "92%", label: "PRN fill rate at 7 days" },
              { num: "≤4h", label: "Day-of-shift fill window" },
              { num: "1 BD", label: "Account approval" },
              { num: "100%", label: "Joint Commission audit pack on demand" },
            ].map((m, i) => (
              <div key={m.label} className="rounded-3xl bg-white/5 border border-white/10 p-6" data-testid={`ff-metric-${i}`}>
                <div className="font-display font-black text-4xl text-white tabular-nums">{m.num}</div>
                <div className="mt-2 text-[13px] text-white/70 leading-relaxed">{m.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PRICING TEASER */}
      <section className="max-w-[1400px] mx-auto px-5 lg:px-8 py-20 lg:py-24" data-testid="ff-pricing">
        <div className="max-w-3xl text-center mx-auto">
          <div className="overline text-trital-red">Pricing</div>
          <h2 className="mt-3 font-display font-black text-4xl lg:text-[44px] tracking-tight leading-[1.05] text-slate-900 text-balance">
            Simple bill-rate. No platform fees.
          </h2>
          <p className="mt-5 text-slate-600 leading-relaxed">All-in hourly bill-rate per clinician. No marketplace markups. No license fees. We earn through a clinician spread, fully disclosed.</p>
        </div>
        <div className="mt-12 grid md:grid-cols-3 gap-4">
          {PRICING.map((p, i) => (
            <div
              key={p.tier}
              className={`rounded-3xl p-7 ${p.featured ? "bg-trital-navy text-white shadow-navy" : "bg-white border border-slate-200 shadow-card"}`}
              data-testid={`ff-pricing-${i}`}
            >
              <div className={`text-[10px] uppercase tracking-[0.2em] font-bold ${p.featured ? "text-white/70" : "text-trital-red"}`}>{p.tier}</div>
              <div className={`mt-3 font-display font-black text-2xl ${p.featured ? "text-white" : "text-slate-900"}`}>{p.blurb}</div>
              <ul className={`mt-5 space-y-2.5 text-[13.5px] ${p.featured ? "text-white/80" : "text-slate-700"}`}>
                {p.lineItems.map((li) => (
                  <li key={li} className="inline-flex items-start gap-2"><BadgeCheck className={`w-4 h-4 mt-0.5 shrink-0 ${p.featured ? "text-trital-red" : "text-trital-navy"}`} strokeWidth={2} />{li}</li>
                ))}
              </ul>
              <Link
                to={SHIFTS_CTAS.facilitySignup}
                className={`mt-7 inline-flex items-center justify-center gap-1.5 w-full px-4 py-3 rounded-full text-[13px] font-semibold ${
                  p.featured ? "bg-white text-trital-navy hover:bg-slate-100" : "bg-trital-navy text-white hover:bg-trital-navy-deep"
                }`}
                data-testid={`ff-pricing-cta-${i}`}
              >
                {p.cta} <ArrowRight className="w-3.5 h-3.5" strokeWidth={1.8} />
              </Link>
            </div>
          ))}
        </div>
      </section>

      {/* CTA BAND */}
      <section className="bg-slate-50 border-t border-slate-200">
        <div className="max-w-[1400px] mx-auto px-5 lg:px-8 py-20 text-center">
          <Clock className="w-7 h-7 text-trital-red mx-auto" strokeWidth={1.8} />
          <h2 className="mt-4 font-display font-black text-4xl lg:text-[44px] tracking-tight leading-[1.05] text-slate-900 text-balance">
            Your next 7 days of PRN demand starts now.
          </h2>
          <p className="mt-4 text-slate-600 max-w-[58ch] mx-auto">Create a facility account, post your first shift, and watch the matched-clinician feed populate in real time.</p>
          <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
            <Link to={SHIFTS_CTAS.facilitySignup} className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full bg-trital-navy text-white text-[14px] font-semibold btn-lift hover:bg-trital-navy-deep" data-testid="ff-bottom-cta">
              Create facility account <ArrowRight className="w-4 h-4" strokeWidth={1.8} />
            </Link>
            <Link to={SHIFTS_CTAS.faq} className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full border border-slate-300 text-slate-700 text-[14px] font-semibold hover:border-trital-navy hover:text-trital-navy hover:bg-white">
              Read the FAQ
            </Link>
          </div>
        </div>
      </section>
    </ShiftsLayout>
  );
}
